import Config from '../core/Config.js';
import db from './db/DexieDB.js';

class AnalyticsServiceClass {
  constructor() {
    this.enabled = false;
    this.sessionId = null;
    this.currentTab = null;
    this.tabStartTs = null;
  }

  init() {
    // Enable only if feature flag + user toggle (localStorage) are on
    try {
      const flag = Config?.FEATURES?.ANALYTICS === true;
      const userPref = (typeof localStorage !== 'undefined' && localStorage.getItem('enableAnalytics') === 'true');
      this.enabled = !!(flag && userPref);
    } catch (_) {
      this.enabled = false;
    }
    return this.enabled;
  }

  isEnabled() {
    return this.enabled === true;
  }

  async startSession(meta = {}) {
    if (!this.init()) return null;
    try {
      const startTs = Date.now();
      const id = await db.sessions.add({ startTs, endTs: null, meta });
      this.sessionId = id;
      return id;
    } catch (_) { return null; }
  }

  async endSession() {
    if (!this.isEnabled() || this.sessionId == null) return false;
    try {
      await db.sessions.update(this.sessionId, { endTs: Date.now() });
      this.sessionId = null;
      return true;
    } catch (_) { return false; }
  }

  async onTabChanged(tabId) {
    if (!this.isEnabled()) return;
    const now = Date.now();
    // Close previous tab timing
    if (this.currentTab && this.tabStartTs) {
      const durationMs = now - this.tabStartTs;
      await this._log('tab_view', { tabId: this.currentTab, durationMs });
    }
    this.currentTab = tabId;
    this.tabStartTs = now;
  }

  async logAction(actionKey, details = {}) {
    if (!this.isEnabled()) return;
    // Avoid clobbering primary key 'id' in Dexie stores when callers pass DOM id
    const { id: elementId, ...rest } = details || {};
    await this._log('action', { actionKey, elementId: elementId || null, ...rest });
  }

  async logPromptSent(promptKey, details = {}) {
    if (!this.isEnabled()) return;
    await this._log('prompt_sent', { promptKey, ...details });
  }

  async logDbLoad(source = 'initiatives', details = {}) {
    if (!this.isEnabled()) return;
    await this._log('db_load', { source, ...details });
  }

  async _log(type, payload = {}) {
    try {
      const { id: _omit, ...safe } = payload || {};
      await db.usage_events.add({
        type,
        tabId: this.currentTab || null,
        ts: Date.now(),
        sessionId: this.sessionId,
        ...safe
      });
    } catch (_) {}
  }

  async exportAll() {
    const [events, msg, app, sessions] = await Promise.all([
      db.usage_events.toArray(),
      db.message_feedback.toArray(),
      db.app_feedback.toArray(),
      db.sessions.toArray()
    ]);
    return { events, message_feedback: msg, app_feedback: app, sessions };
  }

  async getTabDurations() {
    const events = await db.usage_events.where('type').equals('tab_view').toArray();
    const agg = {};
    for (const e of events) {
      const key = e.tabId || 'unknown';
      agg[key] = (agg[key] || 0) + (e.durationMs || 0);
    }
    return Object.entries(agg)
      .map(([tabId, totalMs]) => ({ tabId, totalMs }))
      .sort((a, b) => b.totalMs - a.totalMs);
  }

  async getTopActions(limit = 5) {
    const events = await db.usage_events.where('type').equals('action').toArray();
    const counts = {};
    for (const e of events) {
      const key = e.actionKey || 'unknown';
      counts[key] = (counts[key] || 0) + 1;
    }
    const arr = Object.entries(counts).map(([actionKey, count]) => ({ actionKey, count }));
    arr.sort((a, b) => b.count - a.count);
    return arr.slice(0, limit);
  }

  async exportMessageFeedbackJSONL({ includeText = false } = {}) {
    const rows = await db.message_feedback.orderBy('ts').toArray();
    let chatById = {};
    if (includeText) {
      try {
        // Attempt to map messageId -> content using saved chat history
        const key = (await import('../core/Config.js')).default?.STORAGE_KEYS?.CHAT_HISTORY || 'chat_history';
        const saved = localStorage.getItem(key);
        if (saved) {
          const hist = JSON.parse(saved);
          (hist?.messages || []).forEach(m => { chatById[m.id] = m; });
        }
      } catch (_) {}
    }
    const lines = rows.map(r => {
      const line = {
        type: 'message_feedback',
        ts: r.ts,
        tab: r.tabId,
        message_id: r.messageId,
        prompt_key: r.promptKey || null,
        vote: r.vote || 'none',
        rating: r.rating ?? null,
        comment: r.comment || '',
      };
      if (includeText) {
        const msg = chatById[r.messageId];
        if (msg) {
          line.response_text = msg.sender === 'ai' ? msg.content : null;
          // Attempt to find the preceding user message
          // Not perfect without full conversation, but best-effort when present in saved history
        }
      }
      return JSON.stringify(line);
    });
    return lines.join('\n');
  }

  async exportAppFeedbackJSONL() {
    const rows = await db.app_feedback.orderBy('ts').toArray();
    const lines = rows.map(r => JSON.stringify({ type: 'app_feedback', ts: r.ts, category: r.category, text: r.text || '', contact: r.contact || '' }));
    return lines.join('\n');
  }

  async clearTelemetry() {
    await Promise.all([
      db.usage_events.clear(),
      db.sessions.clear()
    ]);
  }
}

const AnalyticsService = new AnalyticsServiceClass();
export default AnalyticsService;

// Expose in DEV for quick inspection
if (import.meta.env?.DEV) {
  try { window.AnalyticsService = AnalyticsService; } catch (_) {}
}
